/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable no-magic-numbers */
import createGeodeticX from './geodeticX.js';
import type {
  TglElevationCoordinates,
  TglElevationTransformFunction,
} from './tgl.types.js';

export type {
  TglElevationCoordinates,
  TglElevationTransformFunction,
} from './tgl.types.js';

// TODO: This is a duplicate of the TGL implementation in the TileMapPlugin.
// This plugin should probably use the TileMapPlugin instead of duplicating the code.

export interface TglTransformCoordinates {
  x: number;
  y: number;
  errorCode: number;
}

// Maximum extent of the map in meters
const MAX_EXTENT_METER = (2 * Math.PI * 6378137) / Math.pow(2.0, 1);

// meters to radians
function metersToRadians(
  horizontalMeters: number,
  verticalMeters: number
): { x: number; y: number } {
  const pt = {
    x: 0,
    y: 0,
  };
  pt.x = (horizontalMeters / MAX_EXTENT_METER) * Math.PI; // longitude in radians
  pt.y = Math.atan(Math.sinh((verticalMeters / MAX_EXTENT_METER) * Math.PI)); // latitude in radians
  return pt;
}

// radians to degrees
function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

function allocateString(module: any, str: any): number {
  const encoder = new TextEncoder();
  const encodedStr = encoder.encode(str);
  const len = encodedStr.length;
  const ptr = module._malloc(len + 1); // Allocate memory
  const bytes = new Uint8Array(module.wasmMemory.buffer, ptr, len + 1);
  bytes.set(encodedStr);
  bytes[len] = 0; // Null-terminate the string
  return ptr;
}

interface GeodeticXTransformer {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- GeodeticX WASM API
  CreateWithCsib(csibContainer: unknown): void;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- GeodeticX WASM API
  Transform(
    from: unknown,
    a: number,
    b: number,
    c: number,
    to: unknown
  ): number[];
}

let geodeticX: any;
export async function initTgl(): Promise<void> {
  if (!geodeticX) {
    geodeticX = await createGeodeticX();
  }
}

function createTransformer(
  csib: ArrayBuffer,
  geodeticGrids: Map<string, ArrayBuffer> | undefined
): GeodeticXTransformer {
  if (geodeticGrids) {
    for (const grid of geodeticGrids) {
      geodeticX.FS.writeFile(`/${grid[0]}`, new Uint8Array(grid[1]));
    }
  }

  // set path where tgl will find geodetic grid and geoid files
  const pathToGeodata = '/';
  const geoPathPtr = allocateString(geodeticX, pathToGeodata);
  geodeticX._geoSetGeodataPath(geoPathPtr);

  // create transformer to scene projection using scene csib
  const sceneCsibPtr = geodeticX._malloc(csib.byteLength);
  // Copy data to memory
  const sceneCsibDataArray = new Uint8Array(
    geodeticX.wasmMemory.buffer,
    sceneCsibPtr,
    csib.byteLength
  );
  sceneCsibDataArray.set(new Uint8Array(csib));
  const sceneCsibDataContainer = new geodeticX.GEOCsibBlobContainer(
    sceneCsibPtr,
    csib.byteLength
  );

  const transformer =
    new geodeticX.GeodeticXTransformer() as GeodeticXTransformer;
  transformer.CreateWithCsib(sceneCsibDataContainer);
  geodeticX._free(sceneCsibPtr);
  return transformer;
}

/**
 * Creates an elevation transform from a CSIB that still carries its vertical model. If
 * that model is a geoid, its .ggf file must be among `geodeticGrids`, otherwise TGL
 * reports an error.
 *
 * Targets `gctOrientatedNEE` (grid elevation) rather than `gctOrientatedNEH`, which
 * would return the ellipsoidal height untouched.
 */
export const getTglElevationTransformFunction = (
  csib: ArrayBuffer,
  geodeticGrids: Map<string, ArrayBuffer> | undefined
): TglElevationTransformFunction => {
  const transformer = createTransformer(csib, geodeticGrids);

  return (
    x: number,
    y: number,
    ellipsoidalHeight: number
  ): TglElevationCoordinates => {
    const radiansP = metersToRadians(x, y);

    const localResult = transformer.Transform(
      geodeticX.geoCoordinateTypes.gctReferenceGlobalLLH,
      radiansP.y,
      radiansP.x,
      ellipsoidalHeight,
      geodeticX.geoCoordinateTypes.gctOrientatedNEE
    );

    return {
      z: localResult[2],
      errorCode: localResult[3],
    };
  };
};

export type TglTransformFunction = (
  x: number,
  y: number,
  sceneToLonLat?: boolean
) => TglTransformCoordinates;
export const getTglTransformFunction = (
  csib: ArrayBuffer,
  geodeticGrids: Map<string, ArrayBuffer> | undefined
): TglTransformFunction => {
  const transformer = createTransformer(csib, geodeticGrids);

  return (
    x: number,
    y: number,
    sceneToLonLat?: boolean
  ): TglTransformCoordinates => {
    if (!sceneToLonLat) {
      const radiansP = metersToRadians(x, y);

      const localResult: number[] = transformer.Transform(
        geodeticX.geoCoordinateTypes.gctReferenceGlobalLLH,
        radiansP.y,
        radiansP.x,
        0,
        geodeticX.geoCoordinateTypes.gctOrientatedNEH
      ) as number[];

      return {
        x: localResult[1],
        y: localResult[0],
        errorCode: localResult[3],
      };
    } else {
      const latLonResult: number[] = transformer.Transform(
        geodeticX.geoCoordinateTypes.gctOrientatedNEH,
        y,
        x,
        0,
        geodeticX.geoCoordinateTypes.gctReferenceGlobalLLH
      ) as number[];

      return {
        x: radiansToDegrees(latLonResult[1]),
        y: radiansToDegrees(latLonResult[0]),
        errorCode: latLonResult[3],
      };
    }
  };
};
